import { useCallback, useEffect, useState } from 'react';
import { reportsAPI } from '../services/api';
import {
  mapMonthlyDetailRows,
  mapMonthlySummaryRows,
  mapSlotPaymentRows,
  mapTransactionRows,
} from './reportsDataMappers';

export function useReportsData({
  year,
  slotPaymentsMonth,
  transactionFrom,
  transactionTo,
  transactionPage = 1,
}) {
  const [monthlyBookedSlots, setMonthlyBookedSlots] = useState([]);
  const [slotWisePayments, setSlotWisePayments] = useState([]);
  const [slotWiseTransactions, setSlotWiseTransactions] = useState([]);
  const [transactionPagination, setTransactionPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingSlotPayments, setLoadingSlotPayments] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingMonthly(true);
    setError('');

    reportsAPI
      .monthlyBookings({ year })
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to load monthly report');
        setMonthlyBookedSlots(mapMonthlySummaryRows(response.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load monthly report');
      })
      .finally(() => {
        if (!cancelled) setLoadingMonthly(false);
      });

    return () => {
      cancelled = true;
    };
  }, [year]);

  useEffect(() => {
    let cancelled = false;
    setLoadingSlotPayments(true);

    reportsAPI
      .slotPayments({ month: slotPaymentsMonth || undefined })
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to load slot payments');
        setSlotWisePayments(mapSlotPaymentRows(response.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load slot payments');
      })
      .finally(() => {
        if (!cancelled) setLoadingSlotPayments(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slotPaymentsMonth]);

  useEffect(() => {
    let cancelled = false;
    setLoadingTransactions(true);

    reportsAPI
      .transactions({
        from: transactionFrom || undefined,
        to: transactionTo || undefined,
        page: transactionPage,
        limit: 50,
      })
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to load transactions');
        setSlotWiseTransactions(mapTransactionRows(response.data));
        setTransactionPagination(response.pagination || { page: transactionPage, limit: 50, total: 0 });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load transactions');
      })
      .finally(() => {
        if (!cancelled) setLoadingTransactions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [transactionFrom, transactionTo, transactionPage]);

  const loadMonthDetails = useCallback(async (monthKey, page = 1) => {
    const response = await reportsAPI.monthlyBookings({
      month: monthKey,
      include: 'bookings',
      page,
      limit: 100,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load month details');
    }
    return {
      bookings: mapMonthlyDetailRows(response.data),
      pagination: response.pagination || { page, limit: 100, total: response.data?.length || 0 },
    };
  }, []);

  return {
    monthlyBookedSlots,
    slotWisePayments,
    slotWiseTransactions,
    transactionPagination,
    loadingMonthly,
    loadingSlotPayments,
    loadingTransactions,
    error,
    loadMonthDetails,
  };
}
